import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardDocumentCheckIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { teamApi, complianceChecklistApi, examPackageApi } from '../lib/api';
import ChecklistEditor from '../components/ChecklistEditor';
import FeatureGate from '../components/FeatureGate';
import LockedFeatureButton from '../components/LockedFeatureButton';
import ModuleIntroPanel from '../components/help/ModuleIntroPanel';
import ContributingModules from '../components/help/ContributingModules';
import toast from 'react-hot-toast';

export default function ComplianceHubPage() {
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch user's teams
  const { data: teamsData } = useQuery({
    queryKey: ['teams', 'list-unwrapped'],
    queryFn: async () => {
      const response = await teamApi.getTeams();
      return response.data;
    },
  });

  const teams = teamsData?.teams || teamsData || [];

  // Auto-select first team
  const activeTeamId = selectedTeamId || (teams.length > 0 ? teams[0].id : '');

  // Fetch checklists for active team
  const { data: checklistsData } = useQuery({
    queryKey: ['checklists', activeTeamId],
    queryFn: async () => {
      const response = await complianceChecklistApi.getChecklists(activeTeamId);
      return response.data;
    },
    enabled: !!activeTeamId,
  });

  const checklists = checklistsData?.checklists || [];

  const handleGenerateExamPackage = async () => {
    if (!activeTeamId) {
      toast.error('Select a team first');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await examPackageApi.generatePackage(activeTeamId, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `exam_package_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Exam package downloaded');
    } catch (error: any) {
      // When responseType is 'blob', error responses are also Blobs — parse them
      let message = 'Failed to generate exam package';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json.error || json.message || message;
        } catch {
          // Fall through to default message
        }
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      }
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  if (teams.length === 0) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="text-center py-16">
          <ShieldCheckIcon className="h-16 w-16 text-gray-300 mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Compliance Hub</h2>
          <p className="mt-2 text-gray-500">
            Create a team to access compliance management features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheckIcon className="h-8 w-8 text-ocean-600" />
          <h1 className="text-3xl font-bold text-navy-900">Compliance Hub</h1>
        </div>
        <p className="text-gray-600">
          Manage compliance checklists and generate examiner-ready packages for your team.
        </p>
      </div>

      {/* What this is + the cross-module moat */}
      <ModuleIntroPanel moduleKey="compliance-hub" />
      <ContributingModules />

      {/* Team selector */}
      {teams.length > 1 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
          <select
            value={activeTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            className="block w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
          >
            {teams.map((team: any) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* One-Click Exam Package */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-ocean-50 to-blue-50">
          <div className="flex items-center gap-2">
            <ArrowDownTrayIcon className="h-5 w-5 text-ocean-600" />
            <h2 className="text-lg font-semibold text-navy-900">One-Click Exam Package</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Generate a complete ZIP with examiner spreadsheets, approved PDFs, approval records, and audit trail.
          </p>
        </div>
        <div className="p-6">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="block rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="block rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              />
            </div>
            <LockedFeatureButton
              feature="exam_package"
              onClick={handleGenerateExamPackage}
              disabled={isGenerating}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {isGenerating ? 'Generating...' : 'Generate Exam Package'}
            </LockedFeatureButton>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Leave dates blank to include all reports. Package is streamed directly and not stored.
          </p>
        </div>
      </div>

      {/* Compliance Checklists */}
      <FeatureGate feature="compliance_checklists" mode="lock">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-ocean-600" />
              <h2 className="text-lg font-semibold text-navy-900">Review Checklists</h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Create reusable checklist templates that your team applies to each report review.
              Checklists track procedural compliance steps, not institutional policy.
            </p>
          </div>
          <div className="p-6">
            {activeTeamId ? (
              <ChecklistEditor teamId={activeTeamId} checklists={checklists} />
            ) : (
              <p className="text-sm text-gray-500">Select a team to manage checklists.</p>
            )}
          </div>
        </div>
      </FeatureGate>

      {/* Security Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Security & Compliance</h3>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>All checklist actions are recorded in the immutable audit trail</li>
          <li>Exam packages are streamed directly to your browser, not stored on our servers</li>
          <li>Only compliance officers and team owners can create/manage checklist templates</li>
          <li>Checklists store procedural review steps only, not institutional policies</li>
        </ul>
      </div>
    </div>
  );
}
